import {
  Loader,
  Message,
  type MessageModel,
} from '@chatscope/chat-ui-kit-react';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import dateFormat from 'dateformat';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AiOutlineFileExcel,
  AiOutlineFilePdf,
  AiOutlineFilePpt,
  AiOutlineFileText,
  AiOutlineFileWord,
  AiOutlineFileZip,
} from 'react-icons/ai';
import { GrDocumentCsv } from 'react-icons/gr';
import ReactPlayer from 'react-player';

import type { ChatMessage, ChatMessageStatus } from './chatMessageViewTypes';

interface FileIconProps {
  fileType?: string;
}

interface ChatMetaProps {
  createdAt?: string | Date;
  seen: boolean;
  status?: ChatMessageStatus;
  onRetry?: () => void;
}

interface ChatBubbleProps {
  isOutgoing: boolean;
  extraModel?: Partial<MessageModel>;
  children?: ReactNode;
}

interface ChatMessageProps {
  message: ChatMessage;
  isOutgoing: boolean;
  onRetry: (messageId: string) => void;
}

const formatChatTime = (value: string | Date | undefined): string =>
  dateFormat(value ?? new Date(), 'dd/mm/yy, h:MM TT');

const fileIconSx = {
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 22,
  lineHeight: 1,
};

const FileIcon = ({ fileType }: FileIconProps): JSX.Element => {
  switch (fileType) {
    case 'docx':
      return (
        <Box component="span" sx={fileIconSx}>
          <AiOutlineFileWord />
        </Box>
      );
    case 'pdf':
      return (
        <Box component="span" sx={fileIconSx}>
          <AiOutlineFilePdf />
        </Box>
      );
    case 'xlsx':
    case 'pub':
      return (
        <Box component="span" sx={fileIconSx}>
          <AiOutlineFileExcel />
        </Box>
      );
    case 'csv':
      return (
        <Box component="span" sx={fileIconSx}>
          <GrDocumentCsv />
        </Box>
      );
    case 'zip':
    case 'exe':
      return (
        <Box component="span" sx={fileIconSx}>
          <AiOutlineFileZip />
        </Box>
      );
    case 'pptx':
      return (
        <Box component="span" sx={fileIconSx}>
          <AiOutlineFilePpt />
        </Box>
      );
    default:
      return (
        <Box component="span" sx={fileIconSx}>
          <AiOutlineFileText />
        </Box>
      );
  }
};

const ChatMeta = ({
  createdAt,
  seen,
  status,
  onRetry,
}: ChatMetaProps): JSX.Element => {
  const { t } = useTranslation();

  if (status === 'failed') {
    return (
      <Box
        component="span"
        role="alert"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          fontSize: 11,
          lineHeight: 1,
          fontWeight: 600,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 14 }} />
        <span>{t('chat.notSent')}</span>
        <Box
          component="button"
          type="button"
          onClick={onRetry}
          sx={{
            font: 'inherit',
            color: 'inherit',
            textDecoration: 'underline',
            bgcolor: 'transparent',
            border: 0,
            p: 0,
            cursor: 'pointer',
          }}
        >
          {t('chat.retry')}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.75,
        fontSize: 10,
        lineHeight: 1,
        opacity: 0.78,
      }}
    >
      {status === 'sending' ? (
        <span>{t('chat.sending')}</span>
      ) : (
        <span>{formatChatTime(createdAt)}</span>
      )}
      {seen ? <DoneAllIcon sx={{ fontSize: 13, color: 'inherit' }} /> : null}
    </Box>
  );
};

const ChatBubble = ({
  isOutgoing,
  extraModel = {},
  children,
}: ChatBubbleProps): JSX.Element => (
  <Message
    model={{
      direction: isOutgoing ? 'outgoing' : 'incoming',
      type: 'custom',
      position: 'single',
      ...extraModel,
    }}
  >
    <Message.CustomContent>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: 0.75,
          fontFamily: 'inherit',
          color: 'inherit',
        }}
      >
        {children}
      </Box>
    </Message.CustomContent>
  </Message>
);

export const ChatMessageView = ({
  message,
  isOutgoing,
  onRetry,
}: ChatMessageProps): JSX.Element | null => {
  const seen = Boolean(message.isSeen && isOutgoing);
  const meta = (
    <ChatMeta
      createdAt={message.createdAt}
      seen={seen}
      status={message.status}
      onRetry={() => message.uuid && onRetry(message.uuid)}
    />
  );
  const uploading = message.status === 'sending' ? <Loader /> : null;

  switch (message.type) {
    case 'text':
      return (
        <ChatBubble isOutgoing={isOutgoing}>
          <span>{message.text}</span>
          {meta}
        </ChatBubble>
      );
    case 'video':
      return (
        <ChatBubble isOutgoing={isOutgoing}>
          <ReactPlayer
            url={message.video}
            width="220px"
            height="140px"
            controls
          />
          {uploading}
          {meta}
        </ChatBubble>
      );
    case 'image':
      return (
        <ChatBubble isOutgoing={isOutgoing}>
          <Box
            component="a"
            href={message.image}
            target="_blank"
            rel="noreferrer"
            download={message.imageName}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '0.85rem',
              color: 'inherit',
              textDecoration: 'none',
              wordBreak: 'break-word',
              '&:hover': { textDecoration: 'underline', color: 'inherit' },
            }}
          >
            <Box
              component="img"
              src={message.image}
              alt=""
              sx={{
                display: 'block',
                height: 140,
                width: 220,
                maxWidth: '100%',
                borderRadius: '10px',
                bgcolor: 'background.paper',
                objectFit: 'cover',
              }}
            />
          </Box>
          {uploading}
          {meta}
        </ChatBubble>
      );
    case 'audio':
      return (
        <ChatBubble isOutgoing={isOutgoing}>
          <Box
            component="audio"
            controls
            src={message.audio?.uri}
            sx={{ height: 36, width: 220, maxWidth: '100%' }}
          />
          {uploading}
          {meta}
        </ChatBubble>
      );
    case 'file':
      return (
        <ChatBubble isOutgoing={isOutgoing}>
          <Box
            component="a"
            href={message.file?.url}
            target="_blank"
            rel="noreferrer"
            download={message.file?.fileName}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '0.85rem',
              color: 'inherit',
              textDecoration: 'none',
              wordBreak: 'break-word',
              '&:hover': { textDecoration: 'underline', color: 'inherit' },
            }}
          >
            <FileIcon fileType={message.file?.fileType} />
            <span>{message.file?.fileName}</span>
          </Box>
          {uploading}
          {meta}
        </ChatBubble>
      );
    default: {
      const unexpected: never = message;
      void unexpected;
      return null;
    }
  }
};
